import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, RevealStagger, staggerItem } from "@/components/site/Reveal";
import { useConfig } from "@/context/ConfigContext";
import { useDemo } from "@/context/DemoContext";

export function Ecosystem() {
  const { config } = useConfig();
  const { launchDemo } = useDemo();
  const products = config?.products || [];

  return (
    <section
      id="ecosystem"
      className="relative border-t border-white/10 py-28 lg:py-36"
      data-testid="ecosystem-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs text-white/40">05 // ECOSYSTEM</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display mt-6 max-w-3xl text-4xl font-light leading-tight tracking-tight text-white sm:text-6xl">
            One Company.
            <br />
            <span className="text-white/50">Multiple Intelligent Systems.</span>
          </h2>
        </Reveal>

        <RevealStagger className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => {
            const isFlagship = p.flagship;
            return (
              <motion.div
                key={p.id}
                variants={staggerItem}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 250, damping: 22 }}
                className={`group relative flex min-h-[280px] flex-col justify-between overflow-hidden rounded-3xl border p-8 transition-colors duration-300 ${
                  isFlagship
                    ? "border-white/20 bg-white/[0.04]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid={`product-card-${p.id}`}
              >
                <div
                  className="absolute -right-20 -top-20 h-52 w-52 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-30"
                  style={{ background: "var(--accent)" }}
                />

                <div className="relative flex items-start justify-between">
                  <span
                    className={`font-mono rounded-full border px-3 py-1 text-[10px] tracking-[0.15em] ${
                      isFlagship
                        ? "border-transparent text-white"
                        : "border-white/15 text-white/50"
                    }`}
                    style={
                      isFlagship
                        ? { background: "var(--accent)" }
                        : undefined
                    }
                  >
                    {p.status.toUpperCase()}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-white/30 transition-colors group-hover:text-white" />
                </div>

                <div className="relative">
                  <h3 className="font-display text-3xl font-medium text-white">
                    {p.name}
                  </h3>
                  <p className="font-body mt-3 text-sm leading-relaxed text-white/55">
                    {p.description}
                  </p>
                  {isFlagship && (
                    <button
                      onClick={launchDemo}
                      data-testid={`product-card-${p.id}-demo-btn`}
                      className="font-mono mt-5 inline-flex items-center gap-2 text-xs tracking-[0.15em] text-white transition-opacity hover:opacity-80"
                      style={{ color: "var(--accent)" }}
                    >
                      LAUNCH DEMO <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </RevealStagger>
      </div>
    </section>
  );
}
